import { useEffect, useState } from "react";
import { useChitChatProfile, type ChitChatProfile } from "@/hooks/useChitChatProfile";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CcAvatar, AfroDivider } from "./primitives";
import { toast } from "@/hooks/use-toast";
import { LogOut, Moon, Shield, UserCircle2, Ban } from "lucide-react";

export function SettingsPanel() {
  const { user, signOut } = useAuth();
  const { profile, upsert } = useChitChatProfile();
  const [draft, setDraft] = useState<Partial<ChitChatProfile>>({});
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState<{ blocked_id: string; full_name?: string | null }[]>([]);

  useEffect(() => {
    if (profile) setDraft(profile);
  }, [profile]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("chitchat_blocks")
      .select("blocked_id")
      .then(async ({ data }) => {
        if (!data) return;
        const ids = data.map((b: any) => b.blocked_id);
        if (!ids.length) return setBlocked([]);
        const { data: profs } = await supabase
          .from("profiles")
          .select("user_id, full_name")
          .in("user_id", ids);
        setBlocked(
          ids.map((id) => ({
            blocked_id: id,
            full_name: profs?.find((p: any) => p.user_id === id)?.full_name,
          })),
        );
      });
  }, [user?.id]);

  const onSave = async () => {
    setBusy(true);
    const { error } = await upsert(draft);
    setBusy(false);
    if (error) {
      toast({ title: "Could not save", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Settings saved" });
  };

  const onAvatar = async (file: File) => {
    if (!user) return;
    const path = `${user.id}/avatar-${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from("chitchat-avatars").upload(path, file, { upsert: true });
    if (error) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
      return;
    }
    const { data } = supabase.storage.from("chitchat-avatars").getPublicUrl(path);
    await upsert({ avatar_url: data.publicUrl });
    toast({ title: "Photo updated" });
  };

  const unblock = async (blocked_id: string) => {
    await supabase.from("chitchat_blocks").delete().eq("blocked_id", blocked_id);
    setBlocked((prev) => prev.filter((b) => b.blocked_id !== blocked_id));
  };

  return (
    <div className="chitchat-theme min-h-full p-4 space-y-4">
      <h2 className="text-xl font-bold text-cc-ink">Settings</h2>

      {/* Profile card */}
      <section className="chitchat-surface p-4">
        <div className="flex items-center gap-2 mb-3">
          <UserCircle2 className="h-5 w-5 text-cc-burnt" />
          <h3 className="font-semibold text-cc-ink">Profile</h3>
        </div>
        <div className="flex items-center gap-3 mb-3">
          <CcAvatar src={profile?.avatar_url} name={profile?.display_name} id={user?.id} size={64} />
          <div className="flex-1">
            <Label className="text-xs text-cc-ink-soft">Change photo</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => e.target.files?.[0] && onAvatar(e.target.files[0])}
            />
          </div>
        </div>
        <div className="grid gap-3">
          <div>
            <Label>Display name</Label>
            <Input
              value={draft.display_name ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, display_name: e.target.value }))}
            />
          </div>
          <div>
            <Label>Username</Label>
            <Input
              value={draft.username ?? ""}
              onChange={(e) =>
                setDraft((d) => ({ ...d, username: e.target.value.replace(/[^a-zA-Z0-9_.]/g, "") }))
              }
            />
          </div>
          <div>
            <Label>About</Label>
            <Textarea
              rows={2}
              value={draft.bio ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, bio: e.target.value }))}
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              value={draft.phone ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
            />
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section className="chitchat-surface p-4">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="h-5 w-5 text-cc-burnt" />
          <h3 className="font-semibold text-cc-ink">Privacy</h3>
        </div>
        <div className="space-y-3">
          <PrivacyRow
            label="Last seen"
            value={draft.privacy_last_seen ?? "everyone"}
            onChange={(v) => setDraft((d) => ({ ...d, privacy_last_seen: v as any }))}
          />
          <PrivacyRow
            label="Profile photo"
            value={draft.privacy_photo ?? "everyone"}
            onChange={(v) => setDraft((d) => ({ ...d, privacy_photo: v as any }))}
          />
          <PrivacyRow
            label="Status"
            value={draft.privacy_status ?? "contacts"}
            onChange={(v) => setDraft((d) => ({ ...d, privacy_status: v as any }))}
          />
          <div className="flex items-center justify-between">
            <Label>Read receipts</Label>
            <Switch
              checked={draft.read_receipts_enabled ?? true}
              onCheckedChange={(v) => setDraft((d) => ({ ...d, read_receipts_enabled: v }))}
            />
          </div>
        </div>
      </section>

      {/* Blocked */}
      <section className="chitchat-surface p-4">
        <div className="flex items-center gap-2 mb-3">
          <Ban className="h-5 w-5 text-cc-burnt" />
          <h3 className="font-semibold text-cc-ink">Blocked users</h3>
        </div>
        {blocked.length === 0 ? (
          <p className="text-sm text-cc-ink-soft">You haven't blocked anyone.</p>
        ) : (
          <ul className="space-y-2">
            {blocked.map((b) => (
              <li key={b.blocked_id} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CcAvatar id={b.blocked_id} name={b.full_name} size={32} />
                  <span className="text-sm">{b.full_name || b.blocked_id.slice(0, 8)}</span>
                </div>
                <Button size="sm" variant="ghost" onClick={() => unblock(b.blocked_id)}>
                  Unblock
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Theme */}
      <section className="chitchat-surface p-4">
        <div className="flex items-center gap-2 mb-3">
          <Moon className="h-5 w-5 text-cc-burnt" />
          <h3 className="font-semibold text-cc-ink">Appearance</h3>
        </div>
        <div className="flex items-center justify-between">
          <Label>Dark mode</Label>
          <Switch
            checked={document.documentElement.classList.contains("dark")}
            onCheckedChange={(v) => {
              document.documentElement.classList.toggle("dark", v);
              setDraft((d) => ({ ...d, theme: v ? "dark" : "light" }));
            }}
          />
        </div>
      </section>

      <AfroDivider />

      <div className="flex gap-2">
        <Button className="chitchat-btn-primary flex-1 h-11" disabled={busy} onClick={onSave}>
          {busy ? "Saving..." : "Save changes"}
        </Button>
        <Button variant="outline" className="h-11" onClick={() => signOut()}>
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function PrivacyRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="everyone">Everyone</SelectItem>
          <SelectItem value="contacts">My contacts</SelectItem>
          <SelectItem value="nobody">Nobody</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
